/**
 * Legacy Token Manager
 *
 * Manages the in-memory legacy JWT lifecycle for backward compatibility with
 * the Java API. The token is NEVER persisted to localStorage or any storage —
 * it lives only in a module-scoped variable.
 *
 * Responsibilities:
 * 1. Fetch legacy token from GET /auth/legacy-token (cookie-authenticated)
 * 2. Attach Bearer token to every legacyHttp request via request interceptor
 * 3. Proactively refresh before expiry (setTimeout-based timer)
 * 4. Safety-net per-request expiry check (handles backgrounded tabs)
 * 5. 401 retry with promise deduplication (mutex) to prevent thundering herd
 */

import { isAxiosError } from 'axios';
import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { erebusHttp } from './instances';
import type { AuthFailureHandler, LegacyTokenResponse, SessionRestoreResult } from './types';

// =============================================================================
// Constants
// =============================================================================

/** Refresh the token this many ms before it expires. */
const REFRESH_BUFFER_MS = 60_000;

/** Minimum interval between refresh attempts to prevent spin loops. */
const MIN_REFRESH_INTERVAL_MS = 5_000;

// =============================================================================
// Module State
// =============================================================================

let token: string | null = null;
let tokenExpiresAt = 0;
let refreshTimer: ReturnType<typeof setTimeout> | null = null;
let refreshPromise: Promise<SessionRestoreResult> | null = null;
let lastRefreshAt = 0;
let authFailureHandler: AuthFailureHandler | null = null;
let legacyInstance: AxiosInstance | null = null;

// =============================================================================
// Internal Helpers
// =============================================================================

/** Parse the exp claim from a JWT without a library. */
function parseTokenExpiry(jwt: string): number {
  try {
    const parts = jwt.split('.');
    if (parts.length !== 3) return 0;

    // JWT uses base64url encoding — convert to standard base64 for atob()
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(base64));
    return typeof payload.exp === 'number' ? payload.exp * 1000 : 0;
  } catch {
    return 0;
  }
}

function isTokenExpiringSoon(): boolean {
  if (!token) return true;
  return Date.now() >= tokenExpiresAt - REFRESH_BUFFER_MS;
}

function scheduleProactiveRefresh(): void {
  clearProactiveRefresh();

  if (!token || tokenExpiresAt <= 0) return;

  const delay = tokenExpiresAt - REFRESH_BUFFER_MS - Date.now();
  if (delay <= 0) return;

  refreshTimer = setTimeout(() => {
    refreshTokenOk().catch(() => {
      // Failure handled inside refreshToken via authFailureHandler
    });
  }, delay);
}

function clearProactiveRefresh(): void {
  if (refreshTimer !== null) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
}

function setToken(newToken: string): void {
  token = newToken;
  tokenExpiresAt = parseTokenExpiry(newToken);
  scheduleProactiveRefresh();
}

// =============================================================================
// Core Refresh Logic (with mutex)
// =============================================================================

/**
 * True when Go refused the session: a 401 or a 403. Anything else that fails —
 * no answer, a timeout, a 5xx, a 429 — says nothing about whether the session
 * is still good.
 */
function isSessionRefused(err: unknown): boolean {
  const status = isAxiosError(err) ? err.response?.status : undefined;
  return status === 401 || status === 403;
}

/**
 * Fetches a fresh legacy token from the Go API. Uses promise deduplication:
 * if multiple callers invoke this concurrently, only one HTTP request fires
 * and all callers await the same promise.
 *
 * `notifyOnAnyFailure` decides whether every failure counts as a failed
 * session (true, for refreshes during use) or only a refusal does (false, for
 * a page load). Only the caller that starts the request gets to choose; a
 * caller that joins an in-flight request shares its outcome.
 */
async function refreshToken(notifyOnAnyFailure = true): Promise<SessionRestoreResult> {
  const now = Date.now();

  // Mutex: return existing in-flight promise if one exists
  if (refreshPromise) return refreshPromise;

  // Rate limiting: don't refresh more often than MIN_REFRESH_INTERVAL_MS
  if (now - lastRefreshAt < MIN_REFRESH_INTERVAL_MS) {
    return token !== null ? 'ok' : 'no-session';
  }

  refreshPromise = doRefresh(notifyOnAnyFailure);

  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}

async function doRefresh(notifyOnAnyFailure: boolean): Promise<SessionRestoreResult> {
  lastRefreshAt = Date.now();

  try {
    const { data } = await erebusHttp.get<LegacyTokenResponse>('/auth/legacy-token');

    if (!data.token) {
      await notifyAuthFailure();
      return 'no-session';
    }

    setToken(data.token);
    return 'ok';
  } catch (err) {
    const refused = isSessionRefused(err);
    if (refused || notifyOnAnyFailure) {
      await notifyAuthFailure();
    }
    return refused ? 'no-session' : 'offline';
  }
}

/** True when a refresh got a token. */
async function refreshTokenOk(): Promise<boolean> {
  return (await refreshToken()) === 'ok';
}

async function notifyAuthFailure(): Promise<void> {
  clearLegacyToken();
  if (authFailureHandler) {
    await authFailureHandler();
  }
}

// =============================================================================
// Interceptors
// =============================================================================

/** Extends AxiosRequestConfig with a retry flag to prevent infinite 401 loops. */
interface RetryableConfig extends InternalAxiosRequestConfig {
  _retried?: boolean;
}

/**
 * Request interceptor: attaches the Bearer token and refreshes if expired.
 * Handles backgrounded tabs where the proactive timer may not have fired.
 */
async function requestInterceptor(
  config: InternalAxiosRequestConfig,
): Promise<InternalAxiosRequestConfig> {
  if (isTokenExpiringSoon() && token !== null) {
    await refreshTokenOk();
  }

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
}

/**
 * Response error interceptor: retries once on 401 with a fresh token.
 * Uses the shared refreshToken() mutex so concurrent 401s produce exactly
 * one refresh request.
 */
async function responseErrorInterceptor(error: unknown): Promise<unknown> {
  const axiosError = error as { config?: RetryableConfig; response?: { status: number } };

  if (axiosError.response?.status !== 401 || !axiosError.config) {
    return Promise.reject(error);
  }

  const config = axiosError.config;

  // Already retried — don't loop. Let the app's own interceptor handle it.
  if (config._retried) {
    return Promise.reject(error);
  }

  config._retried = true;
  const refreshed = await refreshTokenOk();

  if (!refreshed) {
    return Promise.reject(error);
  }

  // Retry the original request with the fresh token
  if (!legacyInstance) {
    return Promise.reject(error);
  }
  config.headers.Authorization = `Bearer ${token}`;
  return legacyInstance(config);
}

// =============================================================================
// Public API
// =============================================================================

/** Registers the callback invoked when auth fails irrecoverably. */
export function setAuthFailureHandler(handler: AuthFailureHandler | null): void {
  authFailureHandler = handler;
}

/** Installs request + response interceptors on the legacy axios instance. */
export function installLegacyInterceptors(instance: AxiosInstance): void {
  legacyInstance = instance;
  instance.interceptors.request.use(requestInterceptor);
  instance.interceptors.response.use(undefined, responseErrorInterceptor);
}

/**
 * Fetches the legacy token from the Go API. Call after login succeeds
 * (cookie is set) or on page refresh to restore the session.
 * Returns true if a valid token was obtained.
 */
export async function fetchLegacyToken(): Promise<boolean> {
  // Bypass the mutex for initial fetch — we always want a fresh token
  lastRefreshAt = 0;
  return refreshTokenOk();
}

/**
 * Like fetchLegacyToken, but for a page load, where it reports why there is no
 * token.
 *
 * Only a refusal means 'no-session': a 401 or 403, or a 200 with no token.
 * Anything else — no answer, a timeout, a 5xx, a 429 — returns 'offline' and
 * does NOT call the auth failure handler. A server that is down or busy is not
 * a sign that the session is gone, and logging the user out for it would lose
 * their offline work.
 */
export async function restoreLegacyToken(): Promise<SessionRestoreResult> {
  lastRefreshAt = 0;
  return refreshToken(false);
}

/** Clears the in-memory token and cancels the proactive refresh timer. */
export function clearLegacyToken(): void {
  token = null;
  tokenExpiresAt = 0;
  clearProactiveRefresh();
}

/** Resets all module state. For tests only. */
export function resetTokenManager(): void {
  clearLegacyToken();
  refreshPromise = null;
  lastRefreshAt = 0;
  authFailureHandler = null;
  legacyInstance = null;
}
